import api from '@/services/apiService';
import { assertApiSuccess, type ApiResponse } from '@/types/api';
import type {
  CashflowAccount,
  CashflowAccountListResponse,
  CashflowCategory,
  CashflowSummary,
  CashflowTransaction,
  CashflowTransactionListResponse,
  CashflowYearlyReport,
  CreateAccountPayload,
  CreateManualTransactionPayload,
  UpdateAccountPayload,
  UpdateManualTransactionPayload,
} from '@/types/cashflow';

export const cashflowService = {
  // 1. Get Summary metrics
  async getSummary(month?: number, year?: number): Promise<CashflowSummary> {
    const res = await api.get<ApiResponse<CashflowSummary>>('/community/cashflow/summary', {
      params: { month, year },
    });
    assertApiSuccess(res.data);
    return res.data.data;
  },

  // 2. Accounts / Posisi Dana
  async getAccounts(): Promise<CashflowAccountListResponse> {
    const res = await api.get<ApiResponse<CashflowAccountListResponse>>('/community/cashflow/accounts');
    assertApiSuccess(res.data);
    return res.data.data;
  },

  async activateCashflow(payload: { accounts: CreateAccountPayload[] }): Promise<CashflowAccount[]> {
    const res = await api.post<ApiResponse<CashflowAccount[]>>('/community/cashflow/activate', payload);
    assertApiSuccess(res.data);
    return res.data.data;
  },

  async createAccount(payload: CreateAccountPayload): Promise<CashflowAccount> {
    const res = await api.post<ApiResponse<CashflowAccount>>('/community/cashflow/accounts', payload);
    assertApiSuccess(res.data);
    return res.data.data;
  },

  async updateAccount(id: number, payload: UpdateAccountPayload): Promise<CashflowAccount> {
    const res = await api.put<ApiResponse<CashflowAccount>>(`/community/cashflow/accounts/${id}`, payload);
    assertApiSuccess(res.data);
    return res.data.data;
  },

  async deleteAccount(id: number): Promise<void> {
    const res = await api.delete<ApiResponse<null>>(`/community/cashflow/accounts/${id}`);
    assertApiSuccess(res.data);
  },

  // 3. Transactions
  async getTransactions(params: {
    month?: number;
    year?: number;
    filter?: string;
    page?: number;
    limit?: number;
  }): Promise<CashflowTransactionListResponse> {
    const res = await api.get<ApiResponse<CashflowTransactionListResponse>>('/community/cashflow/transactions', {
      params,
    });
    assertApiSuccess(res.data);
    return res.data.data;
  },

  async createManualTransaction(payload: CreateManualTransactionPayload): Promise<CashflowTransaction> {
    const res = await api.post<ApiResponse<CashflowTransaction>>('/community/cashflow/transactions', payload);
    assertApiSuccess(res.data);
    return res.data.data;
  },

  async updateManualTransaction(id: number, payload: UpdateManualTransactionPayload): Promise<CashflowTransaction> {
    const res = await api.put<ApiResponse<CashflowTransaction>>(`/community/cashflow/transactions/${id}`, payload);
    assertApiSuccess(res.data);
    return res.data.data;
  },

  async deleteManualTransaction(id: number): Promise<void> {
    const res = await api.delete<ApiResponse<null>>(`/community/cashflow/transactions/${id}`);
    assertApiSuccess(res.data);
  },

  // 4. Categories
  async getCategories(type?: 'income' | 'expense'): Promise<CashflowCategory[]> {
    const res = await api.get<ApiResponse<CashflowCategory[]>>('/community/cashflow/categories', {
      params: { type },
    });
    assertApiSuccess(res.data);
    return res.data.data;
  },

  // 5. Yearly Report
  async getYearlyReport(year?: number): Promise<CashflowYearlyReport> {
    const res = await api.get<ApiResponse<CashflowYearlyReport>>('/community/cashflow/yearly-report', {
      params: { year },
    });
    assertApiSuccess(res.data);
    return res.data.data;
  },
};
